import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { Link } from "wouter";
import { ArrowLeft, Clock, Users, Filter, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useMemo } from "react";
import { useEmbedMode, appendEmbedParam } from "@/hooks/useEmbedMode";
import { hasAnyRole } from "@/lib/roles";
import { TenantLogo } from "@/components/TenantLogo";
import { format, addDays, startOfWeek } from "date-fns";
import { de } from "date-fns/locale";

const DAYS_DE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const DAYS_SHORT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const WEEKDAYS = [1, 2, 3, 4, 5]; // Monday to Friday

const PAYMENT_COLORS: Record<string, string> = {
  selbstzahler: "bg-slate-100 border-slate-300 text-slate-700",
  krankenkasse: "bg-primary/10 border-primary/30 text-primary",
  erstattung: "bg-secondary/10 border-secondary/30 text-secondary",
};

const PAYMENT_LABELS: Record<string, string> = {
  selbstzahler: "Selbstzahler",
  krankenkasse: "Krankenkasse",
  erstattung: "Erstattung möglich",
};

// Helper to format date as YYYY-MM-DD
const formatDateForApi = (date: Date) => format(date, "yyyy-MM-dd");

export default function Calendar() {
  const { data: courses } = trpc.course.list.useQuery();
  const { data: instructors } = trpc.instructor.list.useQuery();
  const isEmbedded = useEmbedMode();

  // Week navigation state
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [instructorFilter, setInstructorFilter] = useState<string>("all");
  const [paymentFilter, setPaymentFilter] = useState<string>("all");

  // Calculate week end (Sunday)
  const weekEnd = addDays(currentWeekStart, 6);

  // Fetch sessions for the current week with auto-generation
  const { data: sessions, isLoading } = trpc.session.getForCalendar.useQuery({
    startDate: formatDateForApi(currentWeekStart),
    endDate: formatDateForApi(weekEnd),
    categoryFilter: categoryFilter !== "all" ? categoryFilter : undefined,
    instructorId: instructorFilter !== "all" ? parseInt(instructorFilter) : undefined,
    paymentType: paymentFilter !== "all" ? paymentFilter : undefined,
  });

  // Get unique categories from courses (for filter options)
  const categories = useMemo(() => {
    if (!courses) return [];
    const cats = new Set(courses.map(c => c.category).filter(Boolean));
    return Array.from(cats) as string[];
  }, [courses]);

  // Group sessions by day of week
  const sessionsByDay = useMemo(() => {
    if (!sessions) return {};
    const grouped: Record<number, typeof sessions> = {};

    WEEKDAYS.forEach(dayOfWeek => {
      grouped[dayOfWeek] = sessions.filter(s => {
        const sessionDate = new Date(s.session.sessionDate + "T12:00:00");
        return sessionDate.getDay() === dayOfWeek;
      }).sort((a, b) => {
        // Sort by start time
        return a.course.startTime.localeCompare(b.course.startTime);
      });
    });

    return grouped;
  }, [sessions]);

  const hasActiveFilters = categoryFilter !== "all" || instructorFilter !== "all" || paymentFilter !== "all";

  const clearFilters = () => {
    setCategoryFilter("all");
    setInstructorFilter("all");
    setPaymentFilter("all");
  };

  // Generate time slots for the grid (7:00 - 21:00)
  const timeSlots = Array.from({ length: 15 }, (_, i) => {
    const hour = 7 + i;
    return `${hour.toString().padStart(2, "0")}:00`;
  });

  return (
    <div className="min-h-screen bg-background">
      {!isEmbedded && <Header />}

      <main className="container py-8">
        {/* Back Link */}
        <Link href={appendEmbedParam("/")}>
          <Button variant="ghost" className="mb-6 gap-2 -ml-2">
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Übersicht
          </Button>
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground md:text-4xl">
            Kurskalender
          </h1>
          <p className="mt-2 text-muted-foreground">
            Übersicht aller Kurse im Wochenverlauf
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Filter className="h-4 w-4" />
                Filter:
              </div>

              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Kategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={instructorFilter} onValueChange={setInstructorFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Kursleitung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kursleiter</SelectItem>
                  {instructors?.map(inst => (
                    <SelectItem key={inst.id} value={inst.id.toString()}>{inst.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Zahlungsart" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Zahlungsarten</SelectItem>
                  <SelectItem value="selbstzahler">Selbstzahler</SelectItem>
                  <SelectItem value="krankenkasse">Krankenkasse</SelectItem>
                  <SelectItem value="erstattung">Erstattung möglich</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                  <X className="h-4 w-4" />
                  Filter zurücksetzen
                </Button>
              )}
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 border-t pt-4">
              <span className="text-sm text-muted-foreground">Legende:</span>
              {Object.entries(PAYMENT_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded border ${PAYMENT_COLORS[key]}`} />
                  <span className="text-sm">{label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Vorherige Woche
          </Button>

          <h2 className="text-lg font-semibold text-center sm:whitespace-nowrap">
            {format(currentWeekStart, "d.", { locale: de })} - {format(addDays(currentWeekStart, 4), "d. MMMM yyyy", { locale: de })}
          </h2>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))}
          >
            Nächste Woche
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-[600px]" />
          </div>
        ) : (
          <>
            {/* Desktop Calendar Grid */}
            <div className="hidden lg:block overflow-x-auto">
              <div className="min-w-[900px]">
                {/* Header Row with dates */}
                <div className="grid grid-cols-6 gap-1 mb-2">
                  <div className="p-2 text-center text-sm font-medium text-muted-foreground">
                    Zeit
                  </div>
                  {WEEKDAYS.map((day, idx) => {
                    const dayDate = addDays(currentWeekStart, idx);
                    return (
                      <div key={day} className="p-2 text-center">
                        <span className="font-semibold text-foreground">{DAYS_DE[day]}</span>
                        <span className="ml-2 text-sm text-muted-foreground">
                          {format(dayDate, "d.M.", { locale: de })}
                        </span>
                        <Badge variant="outline" className="ml-2">
                          {sessionsByDay[day]?.length || 0}
                        </Badge>
                      </div>
                    );
                  })}
                </div>

                {/* Time Grid */}
                <div className="relative border rounded-lg overflow-hidden">
                  {/* Time slots background */}
                  {timeSlots.map((time, idx) => (
                    <div
                      key={time}
                      className={`grid grid-cols-6 gap-1 ${idx % 2 === 0 ? 'bg-muted/30' : 'bg-background'}`}
                      style={{ height: '60px' }}
                    >
                      <div className="p-2 text-xs text-muted-foreground border-r">
                        {time}
                      </div>
                      {WEEKDAYS.map(day => (
                        <div key={day} className="border-r last:border-r-0 relative" />
                      ))}
                    </div>
                  ))}

                  {/* Session blocks overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="grid grid-cols-6 h-full">
                      <div /> {/* Time column spacer */}
                      {WEEKDAYS.map(day => {
                        const daySessions = sessionsByDay[day] || [];

                        // Calculate overlapping groups and column positions
                        const getTimeInMinutes = (time: string) => {
                          const [h, m] = time.split(':').map(Number);
                          return h * 60 + m;
                        };

                        // Sort sessions by start time
                        const sortedSessions = [...daySessions].sort((a, b) =>
                          getTimeInMinutes(a.course.startTime) - getTimeInMinutes(b.course.startTime)
                        );

                        // Find overlapping sessions and assign columns
                        const sessionColumns: Map<number, { col: number; totalCols: number }> = new Map();

                        sortedSessions.forEach((item) => {
                          const sessionStart = getTimeInMinutes(item.course.startTime);
                          const sessionEnd = getTimeInMinutes(item.course.endTime);

                          // Find all sessions that overlap with this one
                          const overlapping = sortedSessions.filter((other) => {
                            if (other.session.id === item.session.id) return true;
                            const otherStart = getTimeInMinutes(other.course.startTime);
                            const otherEnd = getTimeInMinutes(other.course.endTime);
                            return sessionStart < otherEnd && sessionEnd > otherStart;
                          });

                          // Assign column based on position in overlapping group
                          const myIndex = overlapping.findIndex(c => c.session.id === item.session.id);
                          sessionColumns.set(item.session.id, {
                            col: myIndex,
                            totalCols: overlapping.length
                          });
                        });

                        return (
                          <div key={day} className="relative">
                            {daySessions.map(item => {
                              const { session, course, instructor } = item;
                              const startHour = parseInt(course.startTime.split(':')[0]);
                              const startMin = parseInt(course.startTime.split(':')[1]);
                              const endHour = parseInt(course.endTime.split(':')[0]);
                              const endMin = parseInt(course.endTime.split(':')[1]);

                              const top = ((startHour - 7) * 60 + startMin);
                              const height = ((endHour - startHour) * 60 + (endMin - startMin));

                              const colInfo = sessionColumns.get(session.id) || { col: 0, totalCols: 1 };
                              const widthPercent = 100 / colInfo.totalCols;
                              const leftPercent = colInfo.col * widthPercent;

                              // Check if session is cancelled
                              const isCancelled = session.isCancelled === 1;

                              return (
                                <Link key={session.id} href={appendEmbedParam(`/course/${course.id}/session/${session.id}`)}>
                                  <div
                                    className={`absolute rounded border-l-4 p-1 cursor-pointer pointer-events-auto transition-all hover:shadow-md hover:z-10 ${isCancelled ? 'bg-red-50 border-red-300 text-red-600 line-through opacity-60' : PAYMENT_COLORS[course.paymentType]}`}
                                    style={{
                                      top: `${top}px`,
                                      height: `${Math.max(height - 2, 30)}px`,
                                      left: `calc(${leftPercent}% + 2px)`,
                                      width: `calc(${widthPercent}% - 4px)`,
                                      borderLeftColor: isCancelled ? '#ef4444' :
                                        course.paymentType === 'krankenkasse' ? 'var(--primary)' :
                                          course.paymentType === 'erstattung' ? 'var(--secondary)' : '#64748b'
                                    }}
                                  >
                                    <p className="text-xs font-medium truncate">{course.name}</p>
                                    <p className="text-xs opacity-75">
                                      {course.startTime} - {course.endTime}
                                    </p>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile/Tablet List View */}
            <div className="lg:hidden space-y-6">
              {WEEKDAYS.map((day, idx) => {
                const daySessions = sessionsByDay[day] || [];
                const dayDate = addDays(currentWeekStart, idx);
                return (
                  <div key={day}>
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-primary">
                      {DAYS_DE[day]}, {format(dayDate, "d.M.", { locale: de })}
                      <Badge variant="outline">{daySessions.length}</Badge>
                    </h3>
                    {daySessions.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center border rounded-lg border-dashed">
                        Keine Kurse
                      </p>
                    ) : (
                      <div className="flex flex-col gap-4">
                        {daySessions.map(item => {
                          const { session, course, instructor } = item;
                          const isCancelled = session.isCancelled === 1;
                          return (
                            <Link key={session.id} href={appendEmbedParam(`/course/${course.id}/session/${session.id}`)} className="block">
                              <Card className={`cursor-pointer transition-all hover:shadow-md border-l-4 ${isCancelled ? 'bg-red-50 border-red-300 opacity-60' : PAYMENT_COLORS[course.paymentType]}`}
                                style={{
                                  borderLeftColor: isCancelled ? '#ef4444' :
                                    course.paymentType === 'krankenkasse' ? 'var(--primary)' :
                                      course.paymentType === 'erstattung' ? 'var(--secondary)' : '#64748b'
                                }}
                              >
                                <CardContent className="py-3">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className={`font-medium ${isCancelled ? 'line-through text-red-600' : ''}`}>{course.name}</p>
                                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <Clock className="h-3 w-3" />
                                          {course.startTime} - {course.endTime}
                                        </span>
                                        {instructor && (
                                          <span className="flex items-center gap-1">
                                            <Users className="h-3 w-3" />
                                            {instructor.name}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <Badge variant="outline" className={`text-xs ${isCancelled ? 'bg-red-100 text-red-600' : ''}`}>
                                      {isCancelled ? 'Abgesagt' : PAYMENT_LABELS[course.paymentType]}
                                    </Badge>
                                  </div>
                                </CardContent>
                              </Card>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Results count */}
            <div className="mt-6 text-center text-sm text-muted-foreground">
              {sessions?.length || 0} {(sessions?.length || 0) === 1 ? 'Termin' : 'Termine'} in dieser Woche
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Header() {
  const { user, loading } = useAuth();

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link href="/" className="flex items-center gap-3">
          <TenantLogo className="h-12 w-auto" />
        </Link>
        <nav className="flex items-center gap-4">
          {!loading && user && hasAnyRole(user.roles, ["admin", "courseadmin"]) && (
            <Link href={appendEmbedParam("/admin")}>
              <Button variant="outline" size="sm">
                Admin
              </Button>
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
